import { useCallback, useMemo } from "react";
import { useHealthRecord } from "../../store";
import { TravelRecord, TravelSnapshot } from "../../domain/travel";
import { fromApiTravel } from "../../network/apis/travelMapper";
import { useAppContext } from "./useAppContext";
import { useServiceBus } from "./useServiceBus";

interface UseTravels {
  travels: TravelRecord[];
  addTravel: (travel: TravelSnapshot) => Promise<boolean>;
  updateTravel: (travelId: string, travel: TravelSnapshot) => Promise<boolean>;
  deleteTravel: (travelId: string) => Promise<boolean>;
}

async function succeeded(action: () => Promise<{ type: "success" | "error" }>): Promise<boolean> {
  try {
    return (await action()).type === "success";
  } catch (error) {
    console.error("Travel action failed:", error);
    return false;
  }
}

export function useTravels(healthRecordId: string): UseTravels {
  const serviceBus = useServiceBus();
  const { organizationId, teamId } = useAppContext();
  const healthRecord = useHealthRecord(healthRecordId);
  const apiTravels = healthRecord?.travels;
  const travels = useMemo(() => (apiTravels ?? []).map(fromApiTravel), [apiTravels]);

  const addTravel = useCallback(
    (travel: TravelSnapshot) =>
      succeeded(() => serviceBus.dispatch({ type: "createTravel", organizationId, teamId, healthRecordId, travel })),
    [healthRecordId, organizationId, serviceBus, teamId],
  );

  const updateTravel = useCallback(
    (travelId: string, travel: TravelSnapshot) =>
      succeeded(() =>
        serviceBus.dispatch({ type: "updateTravel", organizationId, teamId, healthRecordId, travelId, travel }),
      ),
    [healthRecordId, organizationId, serviceBus, teamId],
  );

  const deleteTravel = useCallback(
    (travelId: string) =>
      succeeded(() => serviceBus.dispatch({ type: "deleteTravel", organizationId, teamId, healthRecordId, travelId })),
    [healthRecordId, organizationId, serviceBus, teamId],
  );

  return { travels, addTravel, updateTravel, deleteTravel };
}
